// Data loading functionality for pull-to-load

import { performFulltextSearch } from '../search-api.js';
import { getTranslation } from '../helpers.js';
import { isFulltextTabActive } from './utils.js';
import { 
    getPullToLoadState,
    setPullToLoadState,
    getPullIndicator,
    getAnimationFrameId,
    setAnimationFrameId,
    setTargetDistance,
    setCurrentDisplayDistance
} from './state.js';

/**
 * Load next page (infinite scroll - append results)
 */
export async function loadNextPage() {
    // Only load if fulltext tab is active
    if (!isFulltextTabActive()) {
        return;
    }
    
    const state = getPullToLoadState();
    
    if (state.isLoading) return;
    if (state.currentPage >= state.totalPages) return;
    
    setPullToLoadState({ isLoading: true });
    
    const nextPage = state.currentPage + 1;
    
    // Update URL with page parameter (same as pagination click)
    try {
        const url = new URL(window.location);
        url.searchParams.set('page', String(nextPage));
        window.history.pushState({}, '', url);
    } catch(_) {}
    
    // Find and highlight next page button in pagination (same visual feedback as clicking)
    const nextPageLink = document.querySelector(`a.page-link[data-page="${nextPage}"]`);
    if (nextPageLink) {
        nextPageLink.classList.add('page-link--loading');
    }
    
    // Show loading state
    const pullIndicator = getPullIndicator();
    if (pullIndicator) {
        pullIndicator.classList.add('pull-to-load-loading');
        const textElement = pullIndicator.querySelector('.pull-to-load-text');
        if (textElement) {
            textElement.textContent = getTranslation('loading', 'Loading...');
        }
    }
    
    try {
        // Set flag to append results instead of replacing
        // This will be checked in render-results.js
        window.__isAppendingResults = true;
        
        // Disable scroll animation for infinite scroll mode
        window.__skipScrollAfterRender = true;
        
        await performFulltextSearch(state.query, nextPage);
        
        // Update state
        setPullToLoadState({ currentPage: nextPage });
        
        // Reset pull distance and stop all animations
        setPullToLoadState({ pullDistance: 0 });
        setTargetDistance(0);
        setCurrentDisplayDistance(0);
        const animationFrameId = getAnimationFrameId();
        if (animationFrameId) {
            cancelAnimationFrame(animationFrameId);
            setAnimationFrameId(null);
        }
        
        // Set flag to prevent text flickering after loading
        // This flag will prevent ANY text updates until cleared
        setPullToLoadState({ justFinishedLoading: true });
        
        // CRITICAL: Immediately hide indicator WITHOUT calling updatePullIndicator
        // This prevents any text changes or flickering
        // Direct DOM manipulation to avoid any text update logic
        if (pullIndicator) {
            pullIndicator.style.opacity = '0';
            pullIndicator.style.transform = `translateY(100%)`;
            pullIndicator.classList.remove('pull-to-load-active', 'pull-to-load-ready', 'pull-to-load-loading');
            pullIndicator.style.setProperty('--progress', '0');
            // Hide text element directly to prevent any visibility
            const textElement = pullIndicator.querySelector('.pull-to-load-text');
            if (textElement) {
                textElement.style.display = 'none';
            }
        }
        
        // Clear flag after delay to ensure all animations/updates complete
        // Use longer delay (2s) to catch any late updatePullIndicator calls from animations or scroll events
        setTimeout(() => {
            setPullToLoadState({ justFinishedLoading: false });
            // Restore text element display only if indicator is visible (for next use)
            // Check opacity to ensure indicator is actually visible before restoring text
            const currentIndicator = getPullIndicator();
            if (currentIndicator && currentIndicator.style.opacity !== '0') {
                const textElement = currentIndicator.querySelector('.pull-to-load-text');
                if (textElement) {
                    textElement.style.display = '';
                }
            }
        }, 2000);
        
        // Clear scroll skip flag after rendering
        setTimeout(() => {
            window.__skipScrollAfterRender = false;
        }, 100);
        
        // Clear flag
        window.__isAppendingResults = false;
    } catch (error) {
        console.error('[PullToLoad] Error loading next page:', error);
        window.__isAppendingResults = false;
        const currentIndicator = getPullIndicator();
        if (currentIndicator) {
            const textElement = currentIndicator.querySelector('.pull-to-load-text');
            if (textElement) {
                textElement.textContent = getTranslation('load_error', 'Error loading');
            }
        }
    } finally {
        setPullToLoadState({ isLoading: false });
        
        // Remove loading state from pagination button
        if (nextPageLink) {
            nextPageLink.classList.remove('page-link--loading');
        }
        
        setTimeout(() => {
            const currentIndicator = getPullIndicator();
            if (currentIndicator) {
                currentIndicator.classList.remove('pull-to-load-loading');
            }
        }, 500);
    }
}

